import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff, ShieldCheck, Loader2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

/**
 * FirstPasswordResetModal
 *
 * Non-dismissible modal shown immediately after login when the backend flags
 * the account with `must_change_password=true` (users invited via the Team
 * Invite Wizard). Enforces the same password policy the backend does (12+
 * chars, >=3 character classes) and forces a fresh login on success.
 */

function scorePassword(pw) {
  if (!pw) return { score: 0, label: 'Empty' };
  let s = 0;
  if (pw.length >= 12) s += 1;
  if (pw.length >= 16) s += 1;
  if (/[a-z]/.test(pw)) s += 1;
  if (/[A-Z]/.test(pw)) s += 1;
  if (/[0-9]/.test(pw)) s += 1;
  if (/[^A-Za-z0-9]/.test(pw)) s += 1;
  // 0-6 → map to 0-4 label buckets
  const bucket = Math.min(4, Math.max(0, Math.floor(s * 0.7)));
  return {
    score: bucket,
    label: ['Very weak', 'Weak', 'Fair', 'Strong', 'Excellent'][bucket],
  };
}

export default function FirstPasswordResetModal() {
  const { mustChangePassword, token, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const meter = useMemo(() => scorePassword(next), [next]);
  const passwordsMatch = next.length > 0 && next === confirm;
  const meetsPolicy = useMemo(() => {
    if (next.length < 12) return false;
    let classes = 0;
    if (/[a-z]/.test(next)) classes += 1;
    if (/[A-Z]/.test(next)) classes += 1;
    if (/[0-9]/.test(next)) classes += 1;
    if (/[^A-Za-z0-9]/.test(next)) classes += 1;
    return classes >= 3;
  }, [next]);
  const notReuse = next.length === 0 || next !== current;
  const canSubmit = current.length > 0 && meetsPolicy && passwordsMatch && notReuse && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${base}/api/auth/set-first-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      toast.success('Password updated — please sign in again with your new password.');
      // Best practice: don't auto-login. Clear session, kick to /login.
      setTimeout(() => logout(), 700);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!mustChangePassword) return null;

  return (
    <Dialog open={true} modal={true} onOpenChange={() => { /* non-dismissible */ }}>
      <DialogContent
        className="bg-slate-900 border-slate-800 text-slate-100 max-w-md [&>button.absolute]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="first-password-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-cyan-400" />
            <DialogTitle className="text-white">Set your permanent password</DialogTitle>
          </div>
          <DialogDescription className="text-slate-400 text-sm mt-2">
            For your account&apos;s security, please replace the temporary password
            you were given with one only you know. You&apos;ll be asked to sign in
            again with the new password.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div>
            <Label htmlFor="fpm-current" className="text-slate-300">Temporary password</Label>
            <div className="relative mt-1">
              <Input
                id="fpm-current"
                type={showCurrent ? 'text' : 'password'}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                className="bg-slate-950 border-slate-800 pr-10"
                data-testid="fpm-current-input"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200"
                aria-label={showCurrent ? 'Hide password' : 'Show password'}
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="fpm-new" className="text-slate-300">New password</Label>
            <div className="relative mt-1">
              <Input
                id="fpm-new"
                type={showNext ? 'text' : 'password'}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                className="bg-slate-950 border-slate-800 pr-10"
                data-testid="fpm-new-input"
              />
              <button
                type="button"
                onClick={() => setShowNext((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200"
                aria-label={showNext ? 'Hide password' : 'Show password'}
              >
                {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {next.length > 0 && (
              <div className="mt-2" data-testid="fpm-strength-meter">
                <div className="h-1.5 w-full rounded bg-slate-800 overflow-hidden">
                  <div
                    className={
                      'h-full transition-all ' +
                      (meter.score <= 1
                        ? 'bg-red-500'
                        : meter.score === 2
                          ? 'bg-amber-500'
                          : meter.score === 3
                            ? 'bg-cyan-500'
                            : 'bg-emerald-500')
                    }
                    style={{ width: `${(meter.score + 1) * 20}%` }}
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Strength: {meter.label}</p>
              </div>
            )}
            <ul className="text-[11px] text-slate-500 mt-2 space-y-0.5">
              <li className={next.length >= 12 ? 'text-emerald-400' : ''}>• At least 12 characters</li>
              <li className={meetsPolicy ? 'text-emerald-400' : ''}>• Mix of lowercase, uppercase, digits or symbols (any 3 of 4)</li>
              <li className={notReuse ? 'text-emerald-400' : 'text-red-400'}>• Different from the temporary password</li>
            </ul>
          </div>

          <div>
            <Label htmlFor="fpm-confirm" className="text-slate-300">Confirm new password</Label>
            <Input
              id="fpm-confirm"
              type={showNext ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="bg-slate-950 border-slate-800 mt-1"
              data-testid="fpm-confirm-input"
            />
            {confirm.length > 0 && !passwordsMatch && (
              <p className="text-xs text-red-400 mt-1">Passwords do not match.</p>
            )}
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded p-2" data-testid="fpm-error">
              {error}
            </div>
          )}

          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="fpm-submit-btn"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
            ) : (
              <><KeyRound className="w-4 h-4 mr-2" /> Set new password</>
            )}
          </Button>

          <p className="text-[11px] text-slate-500 text-center">
            You cannot close this window until a new password is set — this
            keeps invited accounts safe from stale temporary credentials.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
